import { Filter, RefreshCw } from 'lucide-react';

/**
 * Barre de filtres flexible pour les pages reporting.
 *
 *  - `fields` active/désactive chaque champ (date_from, date_to, service,
 *    bâtiment, type, statut, criticité).
 *  - Les listes d'options (services, bâtiments, types…) sont passées en props
 *    pour rester découplé des appels API.
 *  - Le bouton "Réinitialiser" met à zéro tous les filtres listés.
 */

export interface ReportingFilterValues {
  date_from: string;
  date_to: string;
  service_id: string;
  batiment_id: string;
  type: string;
  statut: string;
  criticite: string;
  priorite: string;
}

export const EMPTY_REPORTING_FILTERS: ReportingFilterValues = {
  date_from: '',
  date_to: '',
  service_id: '',
  batiment_id: '',
  type: '',
  statut: '',
  criticite: '',
  priorite: '',
};

interface Option {
  value: string;
  label: string;
}

export interface ReportingFiltersProps {
  values: ReportingFilterValues;
  onChange(values: ReportingFilterValues): void;
  onRefresh?(): void;
  fields: {
    dates?: boolean;
    service?: boolean;
    batiment?: boolean;
    type?: boolean;
    statut?: boolean;
    criticite?: boolean;
    priorite?: boolean;
  };
  servicesOptions?: Option[];
  batimentsOptions?: Option[];
  typeOptions?: Option[];
  statutOptions?: Option[];
  criticiteOptions?: Option[];
  prioriteOptions?: Option[];
  typeLabel?: string;
  statutLabel?: string;
}

export function ReportingFilters(props: ReportingFiltersProps) {
  const {
    values,
    onChange,
    onRefresh,
    fields,
    servicesOptions = [],
    batimentsOptions = [],
    typeOptions = [],
    statutOptions = [],
    criticiteOptions = [],
    prioriteOptions = [],
    typeLabel = 'Type',
    statutLabel = 'Statut',
  } = props;

  function update<K extends keyof ReportingFilterValues>(key: K, value: ReportingFilterValues[K]) {
    onChange({ ...values, [key]: value });
  }

  function reset() {
    onChange({ ...EMPTY_REPORTING_FILTERS });
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <div className="flex flex-wrap items-end gap-3">
        {fields.dates && (
          <>
            <Field label="Période — du">
              <input
                type="date"
                value={values.date_from}
                onChange={(e) => update('date_from', e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Période — au">
              <input
                type="date"
                value={values.date_to}
                onChange={(e) => update('date_to', e.target.value)}
                className={inputClass}
              />
            </Field>
          </>
        )}
        {fields.service && (
          <Field label="Service">
            <select
              value={values.service_id}
              onChange={(e) => update('service_id', e.target.value)}
              className={inputClass + ' min-w-[180px]'}
            >
              <option value="">— Tous —</option>
              {servicesOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        {fields.batiment && (
          <Field label="Bâtiment">
            <select
              value={values.batiment_id}
              onChange={(e) => update('batiment_id', e.target.value)}
              className={inputClass + ' min-w-[160px]'}
            >
              <option value="">— Tous —</option>
              {batimentsOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        {fields.type && (
          <Field label={typeLabel}>
            <select
              value={values.type}
              onChange={(e) => update('type', e.target.value)}
              className={inputClass + ' min-w-[140px]'}
            >
              <option value="">— Tous —</option>
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        {fields.statut && (
          <Field label={statutLabel}>
            <select
              value={values.statut}
              onChange={(e) => update('statut', e.target.value)}
              className={inputClass + ' min-w-[140px]'}
            >
              <option value="">— Tous —</option>
              {statutOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        {fields.priorite && (
          <Field label="Priorité">
            <select
              value={values.priorite}
              onChange={(e) => update('priorite', e.target.value)}
              className={inputClass + ' min-w-[110px]'}
            >
              <option value="">— Toutes —</option>
              {prioriteOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        {fields.criticite && (
          <Field label="Criticité">
            <select
              value={values.criticite}
              onChange={(e) => update('criticite', e.target.value)}
              className={inputClass + ' min-w-[130px]'}
            >
              <option value="">— Toutes —</option>
              {criticiteOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center px-3 py-2 text-xs text-gray-600 hover:text-gray-900"
            title="Réinitialiser les filtres"
          >
            <Filter className="w-3.5 h-3.5 mr-1" />
            Réinitialiser
          </button>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              title="Actualiser"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" />
              Actualiser
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const inputClass =
  'px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
